import { useState, useRef, useCallback, useEffect } from 'react';

interface UseInfiniteScrollProps {
	hasNextPage: boolean;
	scrollableRef: React.RefObject<HTMLElement>;
	loadMore: () => void;
}

export const useInfiniteScroll = ({
	hasNextPage,
	scrollableRef,
	loadMore,
}: UseInfiniteScrollProps) => {
	const [showLoadMoreButton, setShowLoadMoreButton] = useState<boolean>(false);
	const isTabbedRef = useRef<boolean>(false);
	const loadMoreButtonRef = useRef<HTMLButtonElement>(null);
	const lastItemRef = useRef<HTMLElement | null>(null);

	const hideLoadMoreButton = useCallback(() => {
		setShowLoadMoreButton(false);
	}, []);

	// Add event listener to hide the load more button when scrolling
	useEffect(() => {
		const scrollableElement = scrollableRef.current;
		scrollableElement?.addEventListener('wheel', hideLoadMoreButton);

		return () => {
			scrollableElement?.removeEventListener('wheel', hideLoadMoreButton);
		};
	}, [hideLoadMoreButton, scrollableRef]);

	const handleLoadMoreButtonClick = useCallback(() => {
		// focus on the last item before loading more content
		lastItemRef.current?.focus();
		setShowLoadMoreButton(false);
		loadMore();
	}, [loadMore]);

	// Handle keyboard navigation to show the load more button when tabbing
	const handleKeyDown: React.KeyboardEventHandler<HTMLElement> = useCallback(
		(event) => {
			isTabbedRef.current = event.key === 'Tab' && !event.shiftKey;
			if (isTabbedRef.current && hasNextPage) {
				setShowLoadMoreButton(true);
			}
		},
		[hasNextPage],
	);

	// Handle blur event to manage focus when tabbing away from the load more button
	const handleBlurCapture: React.FocusEventHandler<HTMLElement> = useCallback((event) => {
		const { target: blurredElement, relatedTarget: nextFocusedElement } = event;
		if (nextFocusedElement === loadMoreButtonRef.current) {
			lastItemRef.current = blurredElement;
		}
	}, []);

	return {
		showLoadMoreButton,
		setShowLoadMoreButton,
		loadMoreButtonRef,
		handleKeyDown,
		handleBlurCapture,
		handleLoadMoreButtonClick,
		loadMore,
	};
};
